import * as React from "react";
import { isAsyncUiNameAllowed } from "@1js/office-online-interactivity";

export class UiPaintTiming {
  private static readonly elementType = "p";
  private static readonly innerText = ".";
  private static readonly elementPosition = "fixed";
  private static readonly elementTop = "0px";
  private static readonly elementLeft = "0";
  private static readonly elementColor = "transparent";
  private static readonly elementFontSize = "1px";
  private static readonly elementUserSelect = "none";
  private static readonly elementOverflow = "hidden";
  private static readonly elementZIndex = "-1";
  private static readonly elementPointerEvents = "none";

  private static _isElementTimingSupported: boolean | undefined = undefined;

  public static get isElementTimingSupported(): boolean | undefined {
    if (UiPaintTiming._isElementTimingSupported === undefined) {
      UiPaintTiming._isElementTimingSupported =
        UiPaintTiming.calcIsElementTimingSupported();
    }
    return UiPaintTiming._isElementTimingSupported;
  }

  public static setIsElementTimingSupportedTestHook(
    value: boolean | undefined
  ) {
    UiPaintTiming._isElementTimingSupported = value;
  }

  private static calcIsElementTimingSupported() {
    if (
      typeof window === "undefined" ||
      (window.PerformanceObserver?.supportedEntryTypes || []).indexOf(
        "element"
      ) < 0
    ) {
      return false;
    }
    return "elementTiming" in new Image();
  }

  private static getElementAttributes(
    elementTimingId: string | null | undefined,
    withoutTransparentStyle: boolean,
    hidden?: boolean,
    interactivityTrackerContext?: string,
    removeElementPostTracking?: boolean
  ): ElementTimingObject | null {
    if (
      hidden ||
      !Boolean(elementTimingId) ||
      !UiPaintTiming.isElementTimingSupported ||
      !isAsyncUiNameAllowed(elementTimingId!)
    ) {
      return null;
    }

    const elementAttributes: ElementTimingObject = {
      elementtiming: elementTimingId as string,
      "data-ui-mutation-time": performance.now().toString(),
      "data-ui-mutation-context-value": interactivityTrackerContext ?? "",
      style: {
        position: UiPaintTiming.elementPosition,
        top: UiPaintTiming.elementTop,
        left: UiPaintTiming.elementLeft,
        fontSize: UiPaintTiming.elementFontSize,
        userSelect: UiPaintTiming.elementUserSelect,
        overflow: UiPaintTiming.elementOverflow,
        zIndex: UiPaintTiming.elementZIndex,
        pointerEvents: UiPaintTiming.elementPointerEvents,
      },
    };
    if (!withoutTransparentStyle) {
      elementAttributes.style.color = UiPaintTiming.elementColor;
    }

    if (removeElementPostTracking) {
      elementAttributes["data-ui-mutation-remove-element"] = true;
    }
    return elementAttributes;
  }

  public static getReactElement(
    elementTimingId: string | null | undefined,
    withoutTransparentStyle: boolean,
    interactivityTrackerContext: string | undefined,
    hidden: boolean
  ): React.ReactElement | null {
    const elementAttributes = this.getElementAttributes(
      elementTimingId,
      withoutTransparentStyle,
      hidden,
      interactivityTrackerContext
    );
    if (!elementAttributes) {
      return null;
    }

    return React.createElement(
      UiPaintTiming.elementType,
      elementAttributes,
      UiPaintTiming.innerText
    );
  }

  public static getHTMLElement(
    elementTimingId: string | undefined,
    withoutTransparentStyle: boolean,
    hidden?: boolean,
    interactivityTrackerContext?: string,
    removeElementPostTracking?: boolean
  ): HTMLElement | null {
    const elementAttributes = this.getElementAttributes(
      elementTimingId,
      withoutTransparentStyle,
      hidden,
      interactivityTrackerContext,
      removeElementPostTracking
    );
    if (!elementAttributes) {
      return null;
    }
    const element = document.createElement(UiPaintTiming.elementType);
    element.innerText = UiPaintTiming.innerText;

    Object.keys(elementAttributes).forEach((key: ElementTimingObjectKey) => {
      const value = elementAttributes[key];
      if (key === "style") {
        Object.keys(value as ElementTimingObjectStyle).forEach(
          (key2: ElementTimingObjectStyleKey) => {
            // TODO - remove this if statement once we remove OfficeVSO:9513221_removeTransparantStyle change gate
            if (typeof elementAttributes.style[key2] === "string") {
              element.style[key2] = elementAttributes.style[key2];
            }
          }
        );
      } else if (value === true) {
        element.setAttribute(key, "true");
      } else {
        element.setAttribute(key, value as string);
      }
    });

    return element;
  }
}

type ElementTimingObject = {
  elementtiming: string;
  "data-ui-mutation-time": string;
  "data-ui-mutation-context-value": string;
  "data-ui-mutation-remove-element"?: boolean;
  style: ElementTimingObjectStyle;
};

type ElementTimingObjectStyle = {
  position: string;
  top: string;
  left: string;
  color?: string;
  fontSize: string;
  userSelect: string;
  overflow: string;
  zIndex: string;
  pointerEvents: string;
};

type ElementTimingObjectStyleKey = keyof ElementTimingObjectStyle;
type ElementTimingObjectKey = keyof ElementTimingObject;
